import { useEffect } from 'react';
import { STORAGE_AUTOSAVE } from '../constants';
import { useSettings, useUI, useEditorState } from '../contexts';

type UseSideEffectsParams = {
  setSelectedNodeId: (id: string | null) => void;
  authStatus: string;
  workingFiles: Record<string, string>;
  isDirty: (id: string) => boolean;
};

export function useSideEffects({
  setSelectedNodeId,
  authStatus,
  workingFiles,
  isDirty,
}: UseSideEffectsParams) {
  // Get settings from context
  const { autoSaveEnabled } = useSettings();

  // Get UI state from context
  const { setStatus } = useUI();

  // Get editor state from context
  const { activeFile } = useEditorState();
  // Persist autosave setting to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_AUTOSAVE, autoSaveEnabled ? 'true' : 'false');
  }, [autoSaveEnabled]);

  // Sync active file with selected node
  useEffect(() => {
    if (activeFile) {
      setSelectedNodeId(activeFile);
    }
  }, [activeFile, setSelectedNodeId]);

  // Sync auth status with status
  useEffect(() => {
    setStatus(authStatus);
  }, [authStatus, setStatus]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasDirty = Object.keys(workingFiles).some((id) => isDirty(id));
      if (!hasDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, workingFiles]);
}
